// src/services/adminEntryService.ts

import { apiClient } from "./apiClient";
import {
  AdminEntryDto,
  AdminEntryFilters,
  AdminEntryPageResponse,
  DeleteEntryByAdminRequest,
  UpdateEntryByAdminRequest,
} from "../types/adminEntry.types";

const BASE_URL     = "/api/admin/entries";
const ENTRIES_URL  = "/api/entries";       // public endpoint reused for admin create

/**
 * Build query string from filter parameters.
 */
const buildQueryString = (
  page: number,
  size: number,
  filters: AdminEntryFilters
): string => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());
  params.append("sortBy", filters.sortBy);
  params.append("sortDir", filters.sortDir);

  if (filters.search)         params.append("search",         filters.search);
  if (filters.authorUsername) params.append("authorUsername", filters.authorUsername);
  if (filters.targetTitle)    params.append("targetTitle",    filters.targetTitle);
  if (filters.targetType)     params.append("targetType",     filters.targetType);
  if (filters.startDate)      params.append("startDate", filters.startDate);
  if (filters.endDate)        params.append("endDate",   filters.endDate);

  return params.toString();
};

/**
 * Get paginated list of active (non-deleted) entries with optional filters.
 */
export const getAdminEntries = async (
  page: number = 0,
  size: number = 20,
  filters: AdminEntryFilters = { showDeleted: false, showEdited: false, sortBy: "createdAt", sortDir: "desc" }
): Promise<AdminEntryPageResponse> => {
  const qs = buildQueryString(page, size, filters);
  return apiClient.get<AdminEntryPageResponse>(`${BASE_URL}?${qs}`);
};

/**
 * Get paginated list of soft-deleted entries (moderation bin).
 */
export const getAdminDeletedEntries = async (
  page: number = 0,
  size: number = 20,
  filters: AdminEntryFilters = { showDeleted: true, showEdited: false, sortBy: "createdAt", sortDir: "desc" }
): Promise<AdminEntryPageResponse> => {
  const qs = buildQueryString(page, size, filters);
  return apiClient.get<AdminEntryPageResponse>(`${BASE_URL}/deleted?${qs}`);
};

/**
 * Get paginated list of active entries edited by an admin/mod (edit audit view).
 */
export const getAdminEditedEntries = async (
  page: number = 0,
  size: number = 20,
  filters: AdminEntryFilters = { showDeleted: false, showEdited: true, sortBy: "editedAt", sortDir: "desc" }
): Promise<AdminEntryPageResponse> => {
  const qs = buildQueryString(page, size, filters);
  return apiClient.get<AdminEntryPageResponse>(`${BASE_URL}/edited?${qs}`);
};

/**
 * Get single entry by ID.
 */
export const getAdminEntryById = async (entryId: number): Promise<AdminEntryDto> => {
  return apiClient.get<AdminEntryDto>(`${BASE_URL}/${entryId}`);
};

/**
 * Create a new entry attributed to the logged-in admin/moderator.
 * Reuses the public POST /api/entries endpoint — the JWT token ensures
 * the entry is owned by the calling admin user.
 */
export const createAdminEntry = async (
  request: { matchId: number; content: string }
): Promise<AdminEntryDto> => {
  return apiClient.post<AdminEntryDto>(ENTRIES_URL, request);
};

/**
 * Update entry content (ADMIN + MODERATOR).
 */
export const updateAdminEntry = async (
  entryId: number,
  request: UpdateEntryByAdminRequest
): Promise<AdminEntryDto> => {
  return apiClient.put<AdminEntryDto>(`${BASE_URL}/${entryId}`, request);
};

/**
 * Soft-delete an entry (ADMIN + MODERATOR).
 * Reason is sent as a query param because apiClient.delete does not support a body.
 */
export const deleteAdminEntry = async (
  entryId: number,
  request?: DeleteEntryByAdminRequest
): Promise<void> => {
  const params = new URLSearchParams();
  if (request?.reason) params.append("reason", request.reason);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiClient.delete<void>(`${BASE_URL}/${entryId}${query}`);
};

/**
 * Restore a soft-deleted entry (ADMIN only).
 */
export const restoreAdminEntry = async (entryId: number): Promise<AdminEntryDto> => {
  return apiClient.post<AdminEntryDto>(`${BASE_URL}/${entryId}/restore`, {});
};

/**
 * Permanently delete an entry and all its votes (ADMIN only).
 */
export const hardDeleteAdminEntry = async (entryId: number): Promise<void> => {
  return apiClient.delete<void>(`${BASE_URL}/${entryId}/hard`);
};